/** Encounter screen two-column workspace layout. Layout only — no Note/Encounter writes. */

/** The Patient Chart column defaults to roughly 40% so Documentation stays dominant. */
export const CHART_DEFAULT_PERCENT = 40;
export const CHART_MIN_PERCENT = 30;
export const CHART_MAX_PERCENT = 45;
export const CHART_COLLAPSED_PX = 40;
export const DIVIDER_PX = 8;
export const DESKTOP_MIN_WIDTH = 1024;
export const CLICK_DRAG_THRESHOLD_PX = 5;

/**
 * Diagnosis/CPT/Order column, on the right. Fixed width when open — unlike
 * the Patient Chart it is not drag-resizable, only collapsible.
 */
export const CODING_DEFAULT_WIDTH_PX = 380;
export const CODING_COLLAPSED_PX = 40;

export function showDiagnosisForNote(note) {
  return Boolean(note?.id);
}

/** CPT belongs to the currently selected Note of any type. Does not create a Note. */
export function showCptForNote(note) {
  return Boolean(note?.id);
}

/** Note header, Sign, SOAP, and actions appear only after a real Note is selected. */
export function shouldShowActiveNoteChrome(selectedNote) {
  return Boolean(selectedNote?.id);
}

export function isClickNotDrag(startX, endX, threshold = CLICK_DRAG_THRESHOLD_PX) {
  return Math.abs(Number(endX) - Number(startX)) < threshold;
}

export function percentFromDelta(deltaPx, containerWidth) {
  const width = Number(containerWidth);
  if (!width) return 0;
  return (Number(deltaPx) / width) * 100;
}

export function chartLayoutMode(viewportWidth) {
  return Number(viewportWidth) < DESKTOP_MIN_WIDTH ? "stacked" : "columns";
}

export function clampChartPercent(value) {
  return Math.min(CHART_MAX_PERCENT, Math.max(CHART_MIN_PERCENT, Number(value)));
}

export function defaultChartLayoutState() {
  return {
    chartPercent: CHART_DEFAULT_PERCENT,
    chartCollapsed: false,
    codingCollapsed: false,
  };
}

export function stackedChartLayoutState() {
  return {
    ...defaultChartLayoutState(),
    chartCollapsed: true,
    codingCollapsed: true,
  };
}

export function initialChartLayoutState(viewportWidth) {
  return chartLayoutMode(viewportWidth) === "stacked"
    ? stackedChartLayoutState()
    : defaultChartLayoutState();
}

export function dragChart(state, nextChartPercent) {
  if (state.chartCollapsed) return state;
  return { ...state, chartPercent: clampChartPercent(nextChartPercent) };
}

export function collapseChart(state) {
  if (state.chartCollapsed) return state;
  return { ...state, chartCollapsed: true };
}

export function expandChart(state) {
  if (!state.chartCollapsed) return state;
  return {
    ...state,
    chartCollapsed: false,
    chartPercent: state.chartPercent || CHART_DEFAULT_PERCENT,
  };
}

export function clickChartDivider(state) {
  return state.chartCollapsed ? expandChart(state) : collapseChart(state);
}

export function chartPaneFlex(state) {
  return state.chartCollapsed
    ? `0 0 ${CHART_COLLAPSED_PX}px`
    : `0 0 ${state.chartPercent}%`;
}

/**
 * The Diagnosis/CPT/Order column on the right. Independent of the Patient
 * Chart column: collapsing or expanding one never touches the other's state.
 */
export function collapseCoding(state) {
  if (state.codingCollapsed) return state;
  return { ...state, codingCollapsed: true };
}

export function expandCoding(state) {
  if (!state.codingCollapsed) return state;
  return { ...state, codingCollapsed: false };
}

export function toggleCodingPanel(state) {
  return state.codingCollapsed ? expandCoding(state) : collapseCoding(state);
}

export function codingPaneFlex(state) {
  return state.codingCollapsed
    ? `0 0 ${CODING_COLLAPSED_PX}px`
    : `0 0 ${CODING_DEFAULT_WIDTH_PX}px`;
}
